import * as R from 'ramda'
import { cloneDeep } from 'lodash'
import i18n from 'i18next'

import MergeFields from '../../../utils/MergeFields'
import { getQuestionTitle, getOptionTitle } from '../../../utils/QuestionHelper'

const reportOption = {
    id: '',
    title: {},
    hasOptionOther: false,
    reportingValue: undefined,
}

const reportQuestion = {
    questionId: '',
    pageQuestionId: '',
    questionTypeCode: '',
    yOptions: [],
    xOptions: undefined,
    kpiType: undefined,
    initialLocale: '',
    isBenchmarked: false,
    title: {
        longTitle: {},
        shortTitle: {},
    },
}

const mergeReplace = (locale, fields) => (content) => (
    MergeFields.replace(content, locale, fields)
)

const pipeReplace = (locale, defaultLocale, initialLocale, reportStore) => (content) => {
    let pipedContent = content
    const { pageQuestionMapNew, questionMap } = reportStore

    const questionPipes = pipedContent.match(/\[Q[a-zA-Z0-9-|\s]*\]/gi)
    const pageQuestionPipes = pipedContent.match(/\[PQ[a-zA-Z0-9-|\s]*\]/gi)

    const replaceMatch = (content, match, question) => {
        if (!question) return content
        const questionTitle = getQuestionTitle({
            content: question.content.shortTitle,
            locale,
            defaultLocale,
            initialLocale,
        })
        return content.replace(match, `[${questionTitle} - ${i18n.t('answer')}]`)
    }

    R.or(questionPipes, []).forEach((match) => {
        const questionId = match.replace(/[[\]Q|\s]/g, '').toLowerCase()
        pipedContent = replaceMatch(pipedContent, match, questionMap.get(questionId))
    })

    R.or(pageQuestionPipes, []).forEach((match) => {
        const pageQuestionId = match.replace(/[[\]PQ|\s]/g, '').toUpperCase()
        pipedContent = replaceMatch(pipedContent, match, pageQuestionMapNew.get(pageQuestionId))
    })

    return pipedContent
}

const mapToXOptions = (question, initialLocale, reportStore) => {
    const { appStore, mergeFields } = reportStore
    const { survey } = appStore
    const { metadata } = survey
    const { locales, defaultLocale } = metadata
    let options
    const optionSet = question.optionSetX
    console.log('mapToXOptions')
    if (optionSet) {
        options = optionSet.options.map((o) => (
            Object.assign.call(
                {},
                o,
                {
                    langs: Object.keys(o.langs)
                        .filter((l) => [initialLocale, ...locales].includes(l))
                        .reduce((obj, l) => {
                            obj[l] = R.pipe(
                                R.pathOr('', ['text']),
                                mergeReplace(l, mergeFields),
                                pipeReplace(l, defaultLocale, initialLocale, reportStore),
                                reportStore.stripHtml,
                            )(getOptionTitle({
                                langs: o.langs,
                                locale: l,
                                defaultLocale,
                                initialLocale,
                            }))
                            return obj
                        }, {}),
                },
            )
        ))
    }
    return options
}

const mapToYOptions = (question, parent, initialLocale, reportStore) => {
    const { appStore, mergeFields } = reportStore
    const { survey } = appStore
    const { metadata } = survey
    const { locales, defaultLocale } = metadata
    let options
    const optionSet = question.optionSet || question.optionSetY
    if (optionSet && question.questionTypeCode !== 'FM') {
        let parentOptions = []
        if (parent) {
            console.log('mapToYOptions with parent')
            parentOptions = parent.optionSet.options
                .filter((o) => !o.isNonReporting)
                .reduce((parentOptions, o) => {
                    const option = cloneDeep(o)
                    option.hasOptionOther = false
                    parentOptions.push(option)
                    return parentOptions
                }, [])
        }
        options = parentOptions.concat(optionSet.options.map((o) => (
            Object.assign.call(
                {},
                o,
                {
                    langs: Object.keys(o.langs)
                        .filter((l) => [initialLocale, ...locales].includes(l))
                        .reduce((obj, l) => {
                            obj[l] = R.pipe(
                                R.pathOr('', ['text']),
                                mergeReplace(l, mergeFields),
                                pipeReplace(l, defaultLocale, initialLocale, reportStore),
                                reportStore.stripHtml,
                            )(getOptionTitle({
                                langs: o.langs,
                                locale: l,
                                defaultLocale,
                                initialLocale,
                            }))
                            return obj
                        }, {}),
                },
            )
        )))
    }
    return options
}

const mapToTitle = (content, initialLocale, reportStore) => {
    const { appStore, mergeFields } = reportStore
    const { survey } = appStore
    const { metadata } = survey
    const { locales, defaultLocale } = metadata
    return ['longTitle', 'shortTitle']
        .filter((key) => R.hasPath([key])(content))
        .reduce((obj, key) => {
            obj[key] = Object.keys(content[key])
                .filter((l) => [initialLocale, ...locales].includes(l))
                .reduce((contentLocales, l) => {
                    contentLocales[l] = R.pipe(
                        R.pathOr('', ['text']),
                        mergeReplace(l, mergeFields),
                        pipeReplace(l, defaultLocale, initialLocale, reportStore),
                        reportStore.stripHtml,
                    )(getQuestionTitle({
                        content: content[key],
                        locale: l,
                        defaultLocale,
                        initialLocale,
                    }))
                    return contentLocales
                }, {})
            return obj
        }, {})
}

const mapToQuestion = (pageQuestionId, reportStore, parent, pageQuestionOptionSetY, pageQuestion) => {
    let result = []
    const { pageQuestionMapNew } = reportStore
    const question = pageQuestionMapNew.get(pageQuestionId.toUpperCase())
    question.isHidden = R.pathOr(undefined, ['isHidden'], pageQuestion)
    question.isDataImport = R.pathOr(undefined, ['isDataImport'], pageQuestion)
    if (question) {
        question.headerGroups = R.pathOr([], ['headerGroups'], parent).concat(
            R.pathOr([], ['headerGroups'], question),
        )

        question.hasHeaderGroups = (
            Array.isArray(question.headerGroups)
                && question.headerGroups.length > 0
        )

        if (pageQuestionOptionSetY) {
            const optionSet = JSON.parse(JSON.stringify(pageQuestionOptionSetY))
            if (question.optionSetY) {
                question.optionSetY.options = optionSet.options.concat(question.optionSetY.options)
            } else {
                question.optionSetY = optionSet
            }
        }

        if (question.questionTypeCode === 'FM') {
            const formContent = mapToTitle(question.content, question.metadata.initialLocale, reportStore)
            result = question.optionSet.map(({ question }) => (Object.assign({}, question, {
                pageQuestionId: pageQuestionId.toUpperCase(),
                formContent,
                formId: pageQuestionId.toUpperCase(),
                questionId: question.id.toUpperCase(),
                content: mapToTitle(question.content, question.metadata.initialLocale, reportStore),
                optionSet: question.optionSet ? (Object.assign.call({}, question.optionSet, {
                    options: mapToYOptions(question, parent, question.metadata.initialLocale, reportStore),
                })) : undefined,
            })))
        } else if (question.questionTypeCode === 'NF') {
            result.push(Object.assign.call({}, question, {
                pageQuestionId: pageQuestionId.toUpperCase(),
                questionId: question.id,
                content: mapToTitle(question.content, question.metadata.initialLocale, reportStore),
                optionSet: question.optionSet ? (Object.assign.call({}, question.optionSet, {
                    options: question.optionSet.map((o) => mapToTitle(o.question.content, question.metadata.initialLocale, reportStore)),
                })) : undefined,
            }))
        } else {
            result.push(Object.assign.call({}, question, {
                pageQuestionId: pageQuestionId.toUpperCase(),
                questionId: question.id,
                content: mapToTitle(question.content, question.metadata.initialLocale, reportStore),
                optionSet: question.optionSet ? (Object.assign.call({}, question.optionSet, {
                    options: mapToYOptions(question, parent, question.metadata.initialLocale, reportStore),
                })) : undefined,
                optionSetY: question.optionSetY ? (Object.assign.call({}, question.optionSetY, {
                    options: mapToYOptions(question, parent, question.metadata.initialLocale, reportStore),
                })) : undefined,
                optionSetX: question.optionSetX ? (Object.assign.call({}, question.optionSetX, {
                    options: mapToXOptions(question, question.metadata.initialLocale, reportStore),
                })) : undefined,
            }))
        }
    }
    return result
}

export {
    reportQuestion,
    mapToQuestion,
}
